// Generated by Claude Code
import { createContext, useContext } from 'react'
import { useDelayGroup, useFocus, useHover, useInteractions, useRole } from '@floating-ui/react'
import type { UseInteractionsReturn } from '@floating-ui/react'
import tokens from '#ynt-design-system/tokens.module.scss'
import { useFloatingDismiss, useFloatingElement } from '#hooks/use-floating-element.ts'
import type {
  FloatingAlign,
  FloatingSide,
  UseFloatingElementOptions,
  UseFloatingElementResult,
} from '#hooks/use-floating-element.ts'

export type TooltipPlacement = FloatingSide
export type TooltipAlign = FloatingAlign

/** Long enough that a pointer crossing the trigger on its way elsewhere never opens it. */
export const DEFAULT_TOOLTIP_OPEN_DELAY = 250
/** Short enough to feel immediate, long enough to survive a jittery pointer. */
export const DEFAULT_TOOLTIP_CLOSE_DELAY = 100

/** Set by `TooltipGroup`: tooltips inside share one warm-up instead of their own delays. */
export const TooltipGroupContext = createContext(false)

const OFFSET = parseFloat(tokens['tooltip-offset'])
const VIEWPORT_MARGIN = parseFloat(tokens['tooltip-viewport-margin'])

export interface UseTooltipOptions
  extends Omit<UseFloatingElementOptions, 'offset' | 'viewportMargin' | 'strategy' | 'matchTriggerWidth'> {
  /** Milliseconds a hover must dwell before the panel opens. Focus opens it at once. */
  openDelay?: number
  closeDelay?: number
}

export interface UseTooltipResult
  extends UseFloatingElementResult,
    Pick<UseInteractionsReturn, 'getReferenceProps' | 'getFloatingProps'> {
  /** True while a warmed-up group opens tooltips at once; the panel then skips its enter animation. */
  instant: boolean
}

/** A hover- and focus-opened tooltip; inside a `TooltipGroup` it shares the group's warm-up. */
export function useTooltip({
  placement = 'top',
  openDelay = DEFAULT_TOOLTIP_OPEN_DELAY,
  closeDelay = DEFAULT_TOOLTIP_CLOSE_DELAY,
  disabled = false,
  ...options
}: UseTooltipOptions = {}): UseTooltipResult {
  const floating = useFloatingElement({
    ...options,
    placement,
    disabled,
    offset: OFFSET,
    viewportMargin: VIEWPORT_MARGIN,
  })

  const { context } = floating
  const grouped = useContext(TooltipGroupContext)
  const group = useDelayGroup(context, { enabled: grouped })
  const hover = useHover(context, {
    enabled: !disabled,
    mouseOnly: true,
    move: false,
    delay: grouped ? group.delay : { open: openDelay, close: closeDelay },
  })
  const focus = useFocus(context, { enabled: !disabled })
  const dismiss = useFloatingDismiss(floating, { referencePress: true })
  const role = useRole(context, { role: 'tooltip' })
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role])

  return { ...floating, getReferenceProps, getFloatingProps, instant: grouped && group.isInstantPhase }
}

export default useTooltip
